import type Page from '../../../view-models/Page'
import type ProductEnum from '../../enums/ProductEnum'
import type Account from '../../models/Account'
import type Address from '../../models/Address'
import type { PropertyRisk } from '../../models/Risk'
import type { IQuestionAnswer } from '../../values/PageAnswers'
import type PropertyClaim from '../../values/Property/PropertyClaim'
import type SpecifiedItem from '../../values/Property/SpecifiedItem'
import type QuoteRequest from '../../values/QuoteRequest'
import type IConfiguration from '@/configurations/config-objects/IConfiguration'
import type { Dictionary } from '@/types'
import type { ClaimsQuestion, DateQuestion, NumberQuestion, RelatedSelectQuestion, SelectQuestion, SpecifiedItemsQuestion, TextQuestion } from '@/view-models/Question'
import moment from 'moment'
import useAnswersStore from '@/stores/answers-store'
import DLDCommonValuesEnum from '../../enums/DLDCommonValuesEnum'
import QuestionEnum from '../../enums/QuestionEnum'
import AdditionalPolicyholder from '../../values/AdditionalPolicyholder'
import PageAnswers, { QuestionAnswer } from '../../values/PageAnswers'
import Property from '../../values/Property/Property'
import PropertyQuoteRequest from '../../values/Property/PropertyQuoteRequest'
import RiskProposer from '../../values/RiskProposer'
import { createAnswerForQuestion, getEffectiveFutureDateRestrictionDays, mapAccountToAnswers } from '../BaseMapper'

export function setupPropertyDependencies(page: Page, isNewBusiness: boolean = false): Page {
  const questions = page.sections.flatMap(s => s.questions)
  const homeOwnedOrRentedQuestion = questions.find(q => q.name === QuestionEnum.homeOwnedOrRented) as SelectQuestion<string, string>
  const buildingsContentsOrBothQuestion = questions.find(q => q.name === QuestionEnum.buildingsContentsOrBoth) as SelectQuestion<string, string>
  const propertyTypeQuestion = questions.find(q => q.name === QuestionEnum.propertyType) as SelectQuestion<string, string>
  const homeTypeQuestion = questions.find(q => q.name === QuestionEnum.homeType) as RelatedSelectQuestion
  const costToRebuildPropertyQuestion = questions.find(q => q.name === QuestionEnum.costToRebuildProperty) as NumberQuestion
  const hasHighValueItems = questions.find(q => q.name === QuestionEnum.hasHighValueItems) as SelectQuestion<string, string>
  const anyUnspecifiedItemsQuestion = questions.find(q => q.name === QuestionEnum.anyUnspecifiedItems) as SelectQuestion<string, string>
  const unspecifiedItemValueQuestion = questions.find(q => q.name === QuestionEnum.unspecifiedItemValue) as TextQuestion
  const replaceEntireContentsQuestion = questions.find(q => q.name === QuestionEnum.costToReplaceContents) as TextQuestion
  const hasPurchasedPropertyQuestion = questions.find(q => q.name === QuestionEnum.hasPurchasedProperty) as SelectQuestion<string, string>
  const dateOfPurchaseQuestion = questions.find(q => q.name === QuestionEnum.dateOfPurchase) as DateQuestion
  const whoLivesAtPropertyQuestion = questions.find(q => q.name === QuestionEnum.whoLivesAtProperty) as SelectQuestion<string, string>
  const howManyAdultsQuestion = questions.find(q => q.name === QuestionEnum.howManyAdults) as SelectQuestion<string, string>
  const howManyChildrenQuestion = questions.find(q => q.name === QuestionEnum.howManyChildren) as SelectQuestion<string, string>
  const approximateBuildYearQuestion = questions.find(q => q.name === QuestionEnum.approximateBuildYear) as NumberQuestion
  const howLongHaveYouLivedAtThePropertyQuestion = questions.find(q => q.name === QuestionEnum.howLongHaveYouLivedAtTheProperty) as SelectQuestion<string, string>
  const specifiedItemsQuestion = questions.find(q => q.name === QuestionEnum.specifiedItemsSubpage) as SpecifiedItemsQuestion
  const alarmProfessionallyInstalledMaintainedQuestion = questions.find(q => q.name === QuestionEnum.alarmProfessionallyInstalledMaintained) as SelectQuestion<string, string>
  const typeOfAlarmQuestion = questions.find(q => q.name === QuestionEnum.typeOfAlarm) as SelectQuestion<string, string>
  const largeTreesNearbyQuestion = questions.find(q => q.name === QuestionEnum.largeTreesNearby) as SelectQuestion<string, string>
  const anyHistoryOfUnderpinningQuestion = questions.find(q => q.name === QuestionEnum.anyHistoryOfUnderpinning) as SelectQuestion<string, string>
  const anyHistoryOfSubsidenceQuestion = questions.find(q => q.name === QuestionEnum.anyHistoryOfSubsidence) as SelectQuestion<string, string>

  if (approximateBuildYearQuestion)
    approximateBuildYearQuestion.max = moment().toDate().getFullYear()

  if (homeTypeQuestion && propertyTypeQuestion) {
    homeTypeQuestion.isVisible = () => propertyTypeQuestion.value !== null
    homeTypeQuestion.dependentQuestions = [propertyTypeQuestion]
  }

  if (typeOfAlarmQuestion && alarmProfessionallyInstalledMaintainedQuestion) {
    alarmProfessionallyInstalledMaintainedQuestion.isVisible = () => typeOfAlarmQuestion.value != null
      && typeOfAlarmQuestion.value !== DLDCommonValuesEnum.PropertyInsuranceTypeOfAlarm.NoAlarm
  }

  if (buildingsContentsOrBothQuestion && homeOwnedOrRentedQuestion && isNewBusiness) {
    buildingsContentsOrBothQuestion.isVisible = () => homeOwnedOrRentedQuestion.value != null
      && (homeOwnedOrRentedQuestion.value === DLDCommonValuesEnum.PropertyInsuranceOwnerhip.OwnedOutright
        || homeOwnedOrRentedQuestion.value === DLDCommonValuesEnum.PropertyInsuranceOwnerhip.Mortgaged)

    homeOwnedOrRentedQuestion.onInputChanged = () => {
      if (homeOwnedOrRentedQuestion.value !== DLDCommonValuesEnum.PropertyInsuranceOwnerhip.OwnedOutright && homeOwnedOrRentedQuestion.value !== DLDCommonValuesEnum.PropertyInsuranceOwnerhip.Mortgaged) {
        buildingsContentsOrBothQuestion.value = DLDCommonValuesEnum.PropertyInsuranceTypeOfPolicy.Contents
      }
    }
  }

  if (buildingsContentsOrBothQuestion && costToRebuildPropertyQuestion) {
    costToRebuildPropertyQuestion.min = 10000
    costToRebuildPropertyQuestion.isVisible = () => buildingsContentsOrBothQuestion.value != null
      && buildingsContentsOrBothQuestion.value !== DLDCommonValuesEnum.PropertyInsuranceTypeOfPolicy.Contents
  }

  if (largeTreesNearbyQuestion)
    largeTreesNearbyQuestion.isVisible = () => IncludesBuildings(buildingsContentsOrBothQuestion)

  if (anyHistoryOfUnderpinningQuestion)
    anyHistoryOfUnderpinningQuestion.isVisible = () => IncludesBuildings(buildingsContentsOrBothQuestion)

  if (anyHistoryOfSubsidenceQuestion)
    anyHistoryOfSubsidenceQuestion.isVisible = () => IncludesBuildings(buildingsContentsOrBothQuestion)

  if (hasHighValueItems)
    hasHighValueItems.isVisible = () => IncludesContents()

  if (specifiedItemsQuestion)
    specifiedItemsQuestion.isVisible = () => IncludesContents() && hasHighValueItems.value === DLDCommonValuesEnum.Boolean.Yes

  if (anyUnspecifiedItemsQuestion)
    anyUnspecifiedItemsQuestion.isVisible = () => IncludesContents()

  if (replaceEntireContentsQuestion)
    replaceEntireContentsQuestion.isVisible = () => IncludesContents()

  if (hasPurchasedPropertyQuestion) {
    dateOfPurchaseQuestion.isVisible = () => hasPurchasedPropertyQuestion.value === DLDCommonValuesEnum.Boolean.Yes
    howLongHaveYouLivedAtThePropertyQuestion.isVisible = () => hasPurchasedPropertyQuestion.value === DLDCommonValuesEnum.Boolean.No
  }

  if (whoLivesAtPropertyQuestion) {
    howManyAdultsQuestion.isVisible = () => whoLivesAtPropertyQuestion.value !== null && whoLivesAtPropertyQuestion.value !== DLDCommonValuesEnum.PropertyInsuranceWhoLivesAtProperty.PolicyholderOnly
    howManyChildrenQuestion.isVisible = () => whoLivesAtPropertyQuestion.value !== null && whoLivesAtPropertyQuestion.value !== DLDCommonValuesEnum.PropertyInsuranceWhoLivesAtProperty.PolicyholderOnly
  }

  if (anyUnspecifiedItemsQuestion)
    unspecifiedItemValueQuestion.isVisible = () => anyUnspecifiedItemsQuestion.value === DLDCommonValuesEnum.Boolean.Yes && IncludesContents()

  const costToReplaceContents = questions.find(q => q.name === QuestionEnum.costToReplaceContents) as NumberQuestion

  if (costToReplaceContents && IncludesContents()) {
    costToReplaceContents.setdependentMaxVisible = () => {
      const unspecifiedItemValue = anyUnspecifiedItemsQuestion.value === DLDCommonValuesEnum.Boolean.Yes ? +unspecifiedItemValueQuestion.display()! : 0
      const highValueItemValue = hasHighValueItems.value === DLDCommonValuesEnum.Boolean.Yes ? specifiedItemsQuestion.total() : 0

      return costToReplaceContents.value! < unspecifiedItemValue + highValueItemValue
    }

    costToReplaceContents.setdependentMaxMessage = () => {
      const unspecifiedItemValue = anyUnspecifiedItemsQuestion.value === DLDCommonValuesEnum.Boolean.Yes ? +unspecifiedItemValueQuestion.display()! : 0
      const highValueItemValue = hasHighValueItems.value === DLDCommonValuesEnum.Boolean.Yes ? specifiedItemsQuestion.total() : 0

      return costToReplaceContents.value! < unspecifiedItemValue + highValueItemValue
        ? 'The entire contents value must be equal to or greater than the sum of specified and unspecified items.'
        : ''
    }
  }
  return page
}

export function IncludesContents(): boolean {
  const answersStore = useAnswersStore()
  const buildingsContentsOrBothQuestion = answersStore.answers.find(q => q.name === QuestionEnum.buildingsContentsOrBoth) as QuestionAnswer<string>

  return buildingsContentsOrBothQuestion && (buildingsContentsOrBothQuestion.value === DLDCommonValuesEnum.PropertyInsuranceTypeOfPolicy.BuildingsAndContents
    || buildingsContentsOrBothQuestion.value === DLDCommonValuesEnum.PropertyInsuranceTypeOfPolicy.Contents)
};

export function IncludesBuildings(buildingsContentsOrBothQuestion: SelectQuestion<string, string>): boolean {
  return buildingsContentsOrBothQuestion && (buildingsContentsOrBothQuestion.value === DLDCommonValuesEnum.PropertyInsuranceTypeOfPolicy.BuildingsAndContents
    || buildingsContentsOrBothQuestion.value === DLDCommonValuesEnum.PropertyInsuranceTypeOfPolicy.Buildings)
};

export function setupSpecifiedItems(page: Page): Page {
  setupCommonDependencies(page)

  const questions = page.sections.flatMap(s => s.questions)
  const replacementCostQuestion = questions.find(q => q.name === QuestionEnum.replacementCost) as NumberQuestion

  if (replacementCostQuestion)
    replacementCostQuestion.max = 999999

  return page
}

export function setupCommonDependencies(page: Page): void {
  const questions = page.sections.flatMap(s => s.questions)
  const dateOfBirth = questions.find(q => q.name === QuestionEnum.dateOfBirth) as DateQuestion
  if (dateOfBirth)
    dateOfBirth.maxDate = () => moment().add(-16, 'years').toDate()
}

export function setupHomeProposerDependencies(page: Page, isNewBusiness: boolean = true, config: IConfiguration, productUniqueId?: ProductEnum): Page {
  setupCommonDependencies(page)

  const questions = page.sections.flatMap(s => s.questions)

  const answersStore = useAnswersStore()
  const buildingsContentsOrBothQuestion = answersStore.answers.find(a => a.name === QuestionEnum.buildingsContentsOrBoth) as QuestionAnswer<string>
  const employmentStatusQuestion = questions.find(q => q.name === QuestionEnum.employmentStatus) as SelectQuestion<string, string>
  const address = questions.find(q => q.name === QuestionEnum.proposerAddress) as SelectQuestion<string, string>
  const sameAsHomeAddress = questions.find(q => q.name === QuestionEnum.sameAsHomeAddress) as SelectQuestion<string, string>
  const occupationQuestion = questions.find(q => q.name === QuestionEnum.occupation) as SelectQuestion<string, string>
  const yearsWithNoClaimsBuilding = questions.find(q => q.name === QuestionEnum.yearsWithNoClaimsBuilding) as SelectQuestion<string, string>
  const yearsWithNoClaimsContents = questions.find(q => q.name === QuestionEnum.yearsWithNoClaimsContents) as SelectQuestion<string, string>
  const buildingsVoluntaryExcess = questions.find(q => q.name === QuestionEnum.buildingsVoluntaryExcess) as SelectQuestion<string, string>
  const contentsVoluntaryExcess = questions.find(q => q.name === QuestionEnum.contentsVoluntaryExcess) as SelectQuestion<string, string>
  const coverStartDate = questions.find(q => q.name === QuestionEnum.coverEffectiveDate || q.name === QuestionEnum.mtaEffectiveDate) as DateQuestion
  const includeAccidentalDamageForBuildings = questions.find(q => q.name === QuestionEnum.includeAccidentalDamageForBuildings) as SelectQuestion<string, string>
  const includeAccidentalDamageForContents = questions.find(q => q.name === QuestionEnum.includeAccidentalDamageForContents) as SelectQuestion<string, string>

  coverStartDate.maxDate = () => new Date()
  coverStartDate.minDate = () => moment().toDate()
  coverStartDate.maxDate = () => moment().add(getEffectiveFutureDateRestrictionDays(config, productUniqueId), 'days').toDate()

  occupationQuestion.options = occupationQuestion.options.filter(o => ![
    DLDCommonValuesEnum.Occupation.Unemployed,
    DLDCommonValuesEnum.Occupation.NotInEmployment,
    DLDCommonValuesEnum.Occupation.NotEmployedDueToDisability,
  ].find(x => x === o.value))
  const businessCategoryQuestion = questions.find(q => q.name === QuestionEnum.businessCategory) as SelectQuestion<string, string>
  businessCategoryQuestion.options = businessCategoryQuestion.options.filter(b => ![
    DLDCommonValuesEnum.BusinessCategory.NotInEmployment,
    DLDCommonValuesEnum.BusinessCategory.NonUnemployed,
    DLDCommonValuesEnum.BusinessCategory.NoneNotEmployedduetoDisability,
  ].find(x => x === b.value))

  occupationQuestion.isVisible = () => employmentStatusQuestion.value != null
    && employmentStatusQuestion.value !== DLDCommonValuesEnum.EmploymentStatus.Retired
    && employmentStatusQuestion.value !== DLDCommonValuesEnum.EmploymentStatus.IndependentMeans
    && employmentStatusQuestion.value !== DLDCommonValuesEnum.EmploymentStatus.HouseholdDuties
    && employmentStatusQuestion.value !== DLDCommonValuesEnum.EmploymentStatus.Unemployed
    && employmentStatusQuestion.value !== DLDCommonValuesEnum.EmploymentStatus.NotEmployedDueToDisability
    && employmentStatusQuestion.value !== DLDCommonValuesEnum.EmploymentStatus.FullOrPartTimeEducation

  const studentTypeQuestion = questions.find(q => q.name === QuestionEnum.studentType) as SelectQuestion<string, string>

  studentTypeQuestion.isVisible = () => (employmentStatusQuestion.value != null
    && employmentStatusQuestion.value === DLDCommonValuesEnum.EmploymentStatus.FullOrPartTimeEducation) || !!occupationQuestion.value?.includes('Student')

  businessCategoryQuestion.isVisible = () => employmentStatusQuestion.value != null
    && employmentStatusQuestion.value !== DLDCommonValuesEnum.EmploymentStatus.Retired
    && employmentStatusQuestion.value !== DLDCommonValuesEnum.EmploymentStatus.IndependentMeans
    && employmentStatusQuestion.value !== DLDCommonValuesEnum.EmploymentStatus.HouseholdDuties
    && employmentStatusQuestion.value !== DLDCommonValuesEnum.EmploymentStatus.Unemployed
    && employmentStatusQuestion.value !== DLDCommonValuesEnum.EmploymentStatus.NotEmployedDueToDisability
    && employmentStatusQuestion.value !== DLDCommonValuesEnum.EmploymentStatus.FullOrPartTimeEducation && !studentTypeQuestion.isVisible()

  const hasSecondaryEmployment = questions.find(q => q.name === QuestionEnum.hasSecondaryEmployment) as SelectQuestion<string, string>
  const secondaryOccupation = questions.find(q => q.name === QuestionEnum.secondaryOccupation) as SelectQuestion<string, string>
  const secondaryBusinessCategory = questions.find(q => q.name === QuestionEnum.secondaryBusinessCategory) as SelectQuestion<string, string>
  if (secondaryOccupation && secondaryBusinessCategory)
    secondaryOccupation.isVisible = secondaryBusinessCategory.isVisible = () => hasSecondaryEmployment.value != null && hasSecondaryEmployment.value === DLDCommonValuesEnum.Boolean.Yes

  address.isVisible = () => sameAsHomeAddress?.value === DLDCommonValuesEnum.Boolean.No

  buildingsVoluntaryExcess.isVisible = () => buildingsContentsOrBothQuestion?.value === DLDCommonValuesEnum.PropertyInsuranceTypeOfPolicy.BuildingsAndContents || buildingsContentsOrBothQuestion?.value === DLDCommonValuesEnum.PropertyInsuranceTypeOfPolicy.Buildings
  contentsVoluntaryExcess.isVisible = () => buildingsContentsOrBothQuestion?.value === DLDCommonValuesEnum.PropertyInsuranceTypeOfPolicy.BuildingsAndContents || buildingsContentsOrBothQuestion?.value === DLDCommonValuesEnum.PropertyInsuranceTypeOfPolicy.Contents

  if (yearsWithNoClaimsBuilding && isNewBusiness) {
    yearsWithNoClaimsBuilding.isVisible = () => buildingsContentsOrBothQuestion?.value === DLDCommonValuesEnum.PropertyInsuranceTypeOfPolicy.BuildingsAndContents
      || buildingsContentsOrBothQuestion?.value === DLDCommonValuesEnum.PropertyInsuranceTypeOfPolicy.Buildings
  }

  if (yearsWithNoClaimsContents && isNewBusiness) {
    yearsWithNoClaimsContents.isVisible = () => buildingsContentsOrBothQuestion?.value === DLDCommonValuesEnum.PropertyInsuranceTypeOfPolicy.BuildingsAndContents
      || buildingsContentsOrBothQuestion?.value === DLDCommonValuesEnum.PropertyInsuranceTypeOfPolicy.Contents
  }

  includeAccidentalDamageForBuildings.isVisible = () => buildingsContentsOrBothQuestion?.value === DLDCommonValuesEnum.PropertyInsuranceTypeOfPolicy.BuildingsAndContents || buildingsContentsOrBothQuestion?.value === DLDCommonValuesEnum.PropertyInsuranceTypeOfPolicy.Buildings
  includeAccidentalDamageForContents.isVisible = () => buildingsContentsOrBothQuestion?.value === DLDCommonValuesEnum.PropertyInsuranceTypeOfPolicy.BuildingsAndContents || buildingsContentsOrBothQuestion?.value === DLDCommonValuesEnum.PropertyInsuranceTypeOfPolicy.Contents

  const hasClaims = questions.find(a => a.name === QuestionEnum.hasClaims) as SelectQuestion<string, string>

  const claimsQuestion = questions.find(q => q.name === QuestionEnum.claims) as ClaimsQuestion

  claimsQuestion.isVisible = claimsQuestion.isShowSubPageOnLoad = () => hasClaims?.value === DLDCommonValuesEnum.Boolean.Yes

  if (!isNewBusiness) {
    hasClaims.isEditDisabledInSummaryMode = () => config.readOnlyClaimsPostNewBusiness ?? false
    claimsQuestion.isEditDisabledInSummaryMode = () => !!(hasClaims?.value === DLDCommonValuesEnum.Boolean.Yes && config.readOnlyClaimsPostNewBusiness)
  }
  return page
}

export function setupHomeAdditionalProposerDependencies(page: Page): Page {
  setupCommonDependencies(page)
  const questions = page.sections.flatMap(s => s.questions)
  const employmentStatusQuestion = questions.find(q => q.name === QuestionEnum.employmentStatus) as SelectQuestion<string, string>
  const occupationQuestion = questions.find(q => q.name === QuestionEnum.occupation) as SelectQuestion<string, string>
  occupationQuestion.options = occupationQuestion.options.filter(o => ![
    DLDCommonValuesEnum.Occupation.Unemployed,
    DLDCommonValuesEnum.Occupation.NotInEmployment,
    DLDCommonValuesEnum.Occupation.NotEmployedDueToDisability,
  ].find(x => x === o.value))
  const businessCategoryQuestion = questions.find(q => q.name === QuestionEnum.businessCategory) as SelectQuestion<string, string>
  businessCategoryQuestion.options = businessCategoryQuestion.options.filter(b => ![
    DLDCommonValuesEnum.BusinessCategory.NotInEmployment,
    DLDCommonValuesEnum.BusinessCategory.NonUnemployed,
    DLDCommonValuesEnum.BusinessCategory.NoneNotEmployedduetoDisability,
  ].find(x => x === b.value))

  occupationQuestion.isVisible = () => employmentStatusQuestion.value != null
    && employmentStatusQuestion.value !== DLDCommonValuesEnum.EmploymentStatus.Retired
    && employmentStatusQuestion.value !== DLDCommonValuesEnum.EmploymentStatus.IndependentMeans
    && employmentStatusQuestion.value !== DLDCommonValuesEnum.EmploymentStatus.HouseholdDuties
    && employmentStatusQuestion.value !== DLDCommonValuesEnum.EmploymentStatus.Unemployed
    && employmentStatusQuestion.value !== DLDCommonValuesEnum.EmploymentStatus.NotEmployedDueToDisability
    && employmentStatusQuestion.value !== DLDCommonValuesEnum.EmploymentStatus.FullOrPartTimeEducation

  const studentTypeQuestion = questions.find(q => q.name === QuestionEnum.studentType) as SelectQuestion<string, string>

  studentTypeQuestion.isVisible = () => (employmentStatusQuestion.value != null
    && employmentStatusQuestion.value === DLDCommonValuesEnum.EmploymentStatus.FullOrPartTimeEducation) || !!occupationQuestion.value?.includes('Student')

  businessCategoryQuestion.isVisible = () => employmentStatusQuestion.value != null
    && employmentStatusQuestion.value !== DLDCommonValuesEnum.EmploymentStatus.Retired
    && employmentStatusQuestion.value !== DLDCommonValuesEnum.EmploymentStatus.IndependentMeans
    && employmentStatusQuestion.value !== DLDCommonValuesEnum.EmploymentStatus.HouseholdDuties
    && employmentStatusQuestion.value !== DLDCommonValuesEnum.EmploymentStatus.Unemployed
    && employmentStatusQuestion.value !== DLDCommonValuesEnum.EmploymentStatus.NotEmployedDueToDisability
    && employmentStatusQuestion.value !== DLDCommonValuesEnum.EmploymentStatus.FullOrPartTimeEducation && !studentTypeQuestion.isVisible()

  const hasSecondaryEmployment = questions.find(q => q.name === QuestionEnum.hasSecondaryEmployment) as SelectQuestion<string, string>
  const secondaryOccupation = questions.find(q => q.name === QuestionEnum.secondaryOccupation) as SelectQuestion<string, string>
  const secondaryBusinessCategory = questions.find(q => q.name === QuestionEnum.secondaryBusinessCategory) as SelectQuestion<string, string>

  if (secondaryOccupation && secondaryBusinessCategory)
    secondaryOccupation.isVisible = secondaryBusinessCategory.isVisible = () => hasSecondaryEmployment.value != null && hasSecondaryEmployment.value === DLDCommonValuesEnum.Boolean.Yes

  return page
}

export function setupHomeClaimsDependencies(page: Page, isRenewal: boolean = false): Page {
  const questions = page.sections.flatMap(s => s.questions)
  const claimDate = questions.find(q => q.name === QuestionEnum.claimDate) as DateQuestion
  const claimAmountQuestion = questions.find(q => q.name === QuestionEnum.claimAmount) as NumberQuestion

  if (claimDate) {
    claimDate.minDate = () => moment().add(-5, 'years').toDate()
    claimDate.maxDate = () => moment().toDate()
  }

  if (claimAmountQuestion)
    claimAmountQuestion.min = 0

  if (isRenewal)
    questions.forEach(q => q.isEditDisabledInSummaryMode = () => true)

  return page
}

export function setupHomeMTAClaimsDependencies(page: Page, config: IConfiguration): Page {
  const questions = page.sections.flatMap(s => s.questions)

  const claimAmountQuestion = questions.find(q => q.name === QuestionEnum.claimAmount) as NumberQuestion

  if (claimAmountQuestion)
    claimAmountQuestion.min = 0

  if (!config.readOnlyClaimsPostNewBusiness) {
    const answersStore = useAnswersStore()
    const claimDate = questions.find(q => q.name === QuestionEnum.claimDate) as DateQuestion
    const coverEffectiveDate = answersStore.answers.find(q => q.name === QuestionEnum.coverEffectiveDate) as QuestionAnswer<Date>

    if (claimDate) {
      claimDate.minDate = () => moment().add(-5, 'years').toDate()
      claimDate.maxDate = () => moment(coverEffectiveDate.value).toDate() < moment().toDate() ? moment(coverEffectiveDate.value).toDate() : moment().toDate()
    }
  }

  questions.forEach(q => q.isEditDisabledInSummaryMode = () => config.readOnlyClaimsPostNewBusiness ?? false)
  return page
}

export function getPropertyQuoteRequest<T extends QuoteRequest>(questions: IQuestionAnswer[], Type: new() => T): T {
  // Personal details
  const title = questions.find(q => q.name === QuestionEnum.title) as QuestionAnswer<string>
  const firstName = questions.find(q => q.name === QuestionEnum.firstname) as QuestionAnswer<string>
  const lastName = questions.find(q => q.name === QuestionEnum.lastName) as QuestionAnswer<string>
  const dateOfBirth = questions.find(q => q.name === QuestionEnum.dateOfBirth) as QuestionAnswer<Date>
  const sameAsHomeAddress = questions.find(q => q.name === QuestionEnum.sameAsHomeAddress) as QuestionAnswer<string>
  const propserAddress = questions.find(q => q.name === QuestionEnum.proposerAddress) as QuestionAnswer<Address>
  const maritalStatus = questions.find(q => q.name === QuestionEnum.maritalStatus) as QuestionAnswer<string>
  const employmentStatus = questions.find(q => q.name === QuestionEnum.employmentStatus) as QuestionAnswer<string>
  const occupation = questions.find(q => q.name === QuestionEnum.occupation) as QuestionAnswer<string>
  const businessCategory = questions.find(q => q.name === QuestionEnum.businessCategory) as QuestionAnswer<string>
  const studentType = questions.find(q => q.name === QuestionEnum.studentType) as QuestionAnswer<string>
  const hasSecondaryEmployment = questions.find(q => q.name === QuestionEnum.hasSecondaryEmployment) as QuestionAnswer<string>
  const secondaryOccupation = questions.find(q => q.name === QuestionEnum.secondaryOccupation) as QuestionAnswer<string>
  const secondaryBusinessCategory = questions.find(q => q.name === QuestionEnum.secondaryBusinessCategory) as QuestionAnswer<string>
  const contactPhoneNumber = questions.find(q => q.name === QuestionEnum.mobilePhoneNumber) as QuestionAnswer<string>
  const email = questions.find(q => q.name === QuestionEnum.customerEmail) as QuestionAnswer<string>
  const hasBeenBankrupt = questions.find(q => q.name === QuestionEnum.everDeclaredBankrupt) as QuestionAnswer<string>
  const hadSpecialConditions = questions.find(q => q.name === QuestionEnum.hadSpecialConditions) as QuestionAnswer<string>
  const insuranceDeclined = questions.find(q => q.name === QuestionEnum.insuranceDeclined) as QuestionAnswer<string>
  const hasCriminalConvictions = questions.find(q => q.name === QuestionEnum.hasCriminalConvictions) as QuestionAnswer<string>
  const hasClaims = questions.find(q => q.name === QuestionEnum.hasClaims) as QuestionAnswer<string>
  const yearsWithNoClaimsBuilding = questions.find(q => q.name === QuestionEnum.yearsWithNoClaimsBuilding) as QuestionAnswer<string>
  const yearsWithNoClaimsContents = questions.find(q => q.name === QuestionEnum.yearsWithNoClaimsContents) as QuestionAnswer<string>
  const buildingsVoluntaryExcess = questions.find(q => q.name === QuestionEnum.buildingsVoluntaryExcess) as QuestionAnswer<string>
  const contentsVoluntaryExcess = questions.find(q => q.name === QuestionEnum.contentsVoluntaryExcess) as QuestionAnswer<string>
  const effectiveDateQuestion = questions.find(q => q.name === QuestionEnum.coverEffectiveDate || q.name === QuestionEnum.mtaEffectiveDate) as QuestionAnswer<Date>

  // Property Details
  const address = questions.find(q => q.name === QuestionEnum.address) as QuestionAnswer<Address>
  const homeOwnedOrRented = questions.find(q => q.name === QuestionEnum.homeOwnedOrRented) as QuestionAnswer<string>
  const buildingsContentsOrBoth = questions.find(q => q.name === QuestionEnum.buildingsContentsOrBoth) as QuestionAnswer<string>
  const propertyType = questions.find(q => q.name === QuestionEnum.propertyType) as QuestionAnswer<string>
  const homeType = questions.find(q => q.name === QuestionEnum.homeType) as QuestionAnswer<string>
  const bedroomCount = questions.find(q => q.name === QuestionEnum.bedroomCount) as QuestionAnswer<string>
  const bathroomCount = questions.find(q => q.name === QuestionEnum.bathroomCount) as QuestionAnswer<string>
  const livingRoomCount = questions.find(q => q.name === QuestionEnum.livingRoomCount) as QuestionAnswer<string>
  const otherRoomCount = questions.find(q => q.name === QuestionEnum.otherRoomCount) as QuestionAnswer<string>
  const approximateBuildYear = questions.find(q => q.name === QuestionEnum.approximateBuildYear) as QuestionAnswer<number>
  const roofMaterial = questions.find(q => q.name === QuestionEnum.roofMaterial) as QuestionAnswer<string>
  const roofFlatness = questions.find(q => q.name === QuestionEnum.roofFlatness) as QuestionAnswer<string>
  const externalWallMaterial = questions.find(q => q.name === QuestionEnum.externalWallMaterial) as QuestionAnswer<string>
  const anyHistoryOfSubsidence = questions.find(q => q.name === QuestionEnum.anyHistoryOfSubsidence) as QuestionAnswer<string>
  const anyHistoryOfUnderpinning = questions.find(q => q.name === QuestionEnum.anyHistoryOfUnderpinning) as QuestionAnswer<string>
  const anyHistoryOfFlooding = questions.find(q => q.name === QuestionEnum.anyHistoryOfFlooding) as QuestionAnswer<string>
  const anyBuildingWorkInProgress = questions.find(q => q.name === QuestionEnum.anyBuildingWorkInProgress) as QuestionAnswer<string>
  const propertyDistanceToWatercourse = questions.find(q => q.name === QuestionEnum.propertyDistanceToWatercourse) as QuestionAnswer<string>
  const propertyIsListedBuilding = questions.find(q => q.name === QuestionEnum.propertyIsListedBuilding) as QuestionAnswer<string>
  const mainHome = questions.find(q => q.name === QuestionEnum.mainHome) as QuestionAnswer<string>
  const homeIsInGoodStateOfRepair = questions.find(q => q.name === QuestionEnum.homeIsInGoodStateOfRepair) as QuestionAnswer<string>
  const largeTreesNearby = questions.find(q => q.name === QuestionEnum.largeTreesNearby) as QuestionAnswer<string>
  const costToRebuildProperty = questions.find(q => q.name === QuestionEnum.costToRebuildProperty) as QuestionAnswer<number>
  const includeAccidentalDamageForBuildings = questions.find(q => q.name === QuestionEnum.includeAccidentalDamageForBuildings) as QuestionAnswer<string>
  const includeAccidentalDamageForContents = questions.find(q => q.name === QuestionEnum.includeAccidentalDamageForContents) as QuestionAnswer<string>

  // Home Contents Occupancy and Security
  const hasHighValueItems = questions.find(q => q.name === QuestionEnum.hasHighValueItems) as QuestionAnswer<string>
  const unspecifiedItemValue = questions.find(q => q.name === QuestionEnum.unspecifiedItemValue) as QuestionAnswer<string>
  const costToReplaceContents = questions.find(q => q.name === QuestionEnum.costToReplaceContents) as QuestionAnswer<number>
  const hasPurchasedProperty = questions.find(q => q.name === QuestionEnum.hasPurchasedProperty) as QuestionAnswer<string>
  const dateOfPurchase = questions.find(q => q.name === QuestionEnum.dateOfPurchase) as QuestionAnswer<Date>
  const howLongAtProperty = questions.find(q => q.name === QuestionEnum.howLongHaveYouLivedAtTheProperty) as QuestionAnswer<string>
  const whoLivesAtProperty = questions.find(q => q.name === QuestionEnum.whoLivesAtProperty) as QuestionAnswer<string>
  const howManyAdults = questions.find(q => q.name === QuestionEnum.howManyAdults) as QuestionAnswer<string>
  const howManyChildren = questions.find(q => q.name === QuestionEnum.howManyChildren) as QuestionAnswer<string>
  const anyAnimals = questions.find(q => q.name === QuestionEnum.anyAnimals) as QuestionAnswer<string>
  const anySmokers = questions.find(q => q.name === QuestionEnum.anySmokers) as QuestionAnswer<string>
  const whenAtProperty = questions.find(q => q.name === QuestionEnum.whenAtProperty) as QuestionAnswer<string>
  const howLongPropertyUnoccupied = questions.find(q => q.name === QuestionEnum.howLongPropertyUnoccupied) as QuestionAnswer<string>
  const anyBusinessUse = questions.find(q => q.name === QuestionEnum.anyBusinessUse) as QuestionAnswer<string>
  const typeOfAlarm = questions.find(q => q.name === QuestionEnum.typeOfAlarm) as QuestionAnswer<string>
  const alarmProfessionallyInstalledMaintained = questions.find(q => q.name === QuestionEnum.alarmProfessionallyInstalledMaintained) as QuestionAnswer<string>
  const typeOfLocks = questions.find(q => q.name === QuestionEnum.typeOfLocks) as QuestionAnswer<string>
  const lessSecureDoors = questions.find(q => q.name === QuestionEnum.lessSecureDoors) as QuestionAnswer<string>
  const otherTypeOfLocks = questions.find(q => q.name === QuestionEnum.otherTypeOfLocks) as QuestionAnswer<string>
  const hasKeyOperatedLocksOnAllWindows = questions.find(q => q.name === QuestionEnum.hasKeyOperatedLocksOnAllWindows) as QuestionAnswer<string>
  const workingSmokeDetectors = questions.find(q => q.name === QuestionEnum.workingSmokeDetectors) as QuestionAnswer<string>
  const inVerifiedNeighbourhoodWatchArea = questions.find(q => q.name === QuestionEnum.inVerifiedNeighbourhoodWatchArea) as QuestionAnswer<string>
  const anyUnspecifiedItemsQuestion = questions.find(q => q.name === QuestionEnum.anyUnspecifiedItems) as QuestionAnswer<string>

  if (employmentStatus?.value === DLDCommonValuesEnum.EmploymentStatus.Retired) {
    if (occupation)
      occupation.value = DLDCommonValuesEnum.Occupation.Retired
    if (businessCategory)
      businessCategory.value = DLDCommonValuesEnum.BusinessCategory.NonRetired
  }
  else if (employmentStatus?.value === DLDCommonValuesEnum.EmploymentStatus.Unemployed) {
    if (occupation)
      occupation.value = DLDCommonValuesEnum.Occupation.Unemployed
    if (businessCategory)
      businessCategory.value = DLDCommonValuesEnum.BusinessCategory.NonUnemployed
  }
  else if (employmentStatus?.value === DLDCommonValuesEnum.EmploymentStatus.FinanciallyAssisted || employmentStatus?.value === DLDCommonValuesEnum.EmploymentStatus.IndependentMeans) {
    if (occupation)
      occupation.value = DLDCommonValuesEnum.Occupation.NotInEmployment
    if (businessCategory)
      businessCategory.value = DLDCommonValuesEnum.BusinessCategory.NotInEmployment
  }
  else if (employmentStatus?.value === DLDCommonValuesEnum.EmploymentStatus.NotEmployedDueToDisability) {
    if (occupation)
      occupation.value = DLDCommonValuesEnum.Occupation.NotEmployedDueToDisability
    if (businessCategory)
      businessCategory.value = DLDCommonValuesEnum.BusinessCategory.NoneNotEmployedduetoDisability
  }
  else if (employmentStatus?.value === DLDCommonValuesEnum.EmploymentStatus.HouseholdDuties) {
    if (occupation) {
      occupation.value = (title.value === DLDCommonValuesEnum.Title.Mr || title.value === DLDCommonValuesEnum.Title.DoctorMale)
        ? DLDCommonValuesEnum.Occupation.Househusband
        : DLDCommonValuesEnum.Occupation.Housewife
    }
    if (businessCategory)
      businessCategory.value = DLDCommonValuesEnum.BusinessCategory.NoneHouseholdDuties
  }

  if (employmentStatus?.value === DLDCommonValuesEnum.EmploymentStatus.FullOrPartTimeEducation) {
    if (occupation)
      occupation.value = studentType.value!
    if (businessCategory)
      businessCategory.value = DLDCommonValuesEnum.BusinessCategory.NoneStudent
  }
  else if (occupation?.value?.toLowerCase().includes('student')) {
    if (businessCategory)
      businessCategory.value = DLDCommonValuesEnum.BusinessCategory.NoneStudent
  }
  else {
    if (studentType)
      studentType.value = null
  }

  const quoteRequest = new Type()

  if (!(quoteRequest instanceof PropertyQuoteRequest))
    throw new Error(`Unsupported quote request (${typeof quoteRequest}) for Zen.`)

  quoteRequest.riskProposer = new RiskProposer()
  quoteRequest.riskProposer.title = title.value!
  quoteRequest.riskProposer.firstName = firstName.value!
  quoteRequest.riskProposer.lastName = lastName.value!
  quoteRequest.riskProposer.dateOfBirth = dateOfBirth.value!
  quoteRequest.riskProposer.maritalStatus = maritalStatus.value!
  quoteRequest.riskProposer.employmentStatus = employmentStatus.value!
  quoteRequest.riskProposer.occupation = occupation.value!
  quoteRequest.riskProposer.businessType = businessCategory.value!
  quoteRequest.riskProposer.studentType = studentType.value!
  quoteRequest.riskProposer.hasSecondaryEmployment = hasSecondaryEmployment.value! === DLDCommonValuesEnum.Boolean.Yes
  quoteRequest.riskProposer.secondaryOccupation = hasSecondaryEmployment.value! === DLDCommonValuesEnum.Boolean.Yes ? secondaryOccupation.value! : null
  quoteRequest.riskProposer.secondaryBusinessType = hasSecondaryEmployment.value! === DLDCommonValuesEnum.Boolean.Yes ? secondaryBusinessCategory.value! : null
  quoteRequest.riskProposer.telephoneNumber = contactPhoneNumber.value!
  quoteRequest.riskProposer.emailAddress = email.value!
  quoteRequest.riskProposer.hasDeclaredBankruptcy = hasBeenBankrupt.value! === DLDCommonValuesEnum.Boolean.Yes
  quoteRequest.riskProposer.hadSpecialTermsImposed = hadSpecialConditions.value! === DLDCommonValuesEnum.Boolean.Yes
  quoteRequest.riskProposer.hadInsuranceRefusedCancelledOrDeclined = insuranceDeclined.value! === DLDCommonValuesEnum.Boolean.Yes
  quoteRequest.riskProposer.hasUnspentNonMotoringConvictions = hasCriminalConvictions.value! === DLDCommonValuesEnum.Boolean.Yes
  quoteRequest.riskProposer.hasClaims = hasClaims.value! === DLDCommonValuesEnum.Boolean.Yes
  quoteRequest.riskProposer.voluntaryExcess = (buildingsContentsOrBoth.value === DLDCommonValuesEnum.PropertyInsuranceTypeOfPolicy.Buildings || buildingsContentsOrBoth.value === DLDCommonValuesEnum.PropertyInsuranceTypeOfPolicy.BuildingsAndContents) ? buildingsVoluntaryExcess.value! : null
  quoteRequest.riskProposer.voluntaryExcess2 = (buildingsContentsOrBoth.value === DLDCommonValuesEnum.PropertyInsuranceTypeOfPolicy.Contents || buildingsContentsOrBoth.value === DLDCommonValuesEnum.PropertyInsuranceTypeOfPolicy.BuildingsAndContents) ? contentsVoluntaryExcess.value! : null

  if (sameAsHomeAddress.value! === DLDCommonValuesEnum.Boolean.No)
    quoteRequest.riskProposer.address = propserAddress.value!
  else
    quoteRequest.riskProposer.address = address.value!

  quoteRequest.yearsWithNoClaimsBuilding = (buildingsContentsOrBoth.value === DLDCommonValuesEnum.PropertyInsuranceTypeOfPolicy.Buildings || buildingsContentsOrBoth.value === DLDCommonValuesEnum.PropertyInsuranceTypeOfPolicy.BuildingsAndContents) ? yearsWithNoClaimsBuilding.value! : null
  quoteRequest.yearsWithNoClaimsContents = (buildingsContentsOrBoth.value === DLDCommonValuesEnum.PropertyInsuranceTypeOfPolicy.Contents || buildingsContentsOrBoth.value === DLDCommonValuesEnum.PropertyInsuranceTypeOfPolicy.BuildingsAndContents) ? yearsWithNoClaimsContents.value! : null
  quoteRequest.effectiveDate = effectiveDateQuestion.value!
  quoteRequest.address = address.value!

  const property = new Property()
  property.address = address.value!
  property.own = homeOwnedOrRented.value!
  property.typeOfPolicy = buildingsContentsOrBoth.value!
  property.propertyType = propertyType.value!
  property.subType = homeType.value!
  property.numberOfBedrooms = bedroomCount.value!
  property.numberOfBathrooms = bathroomCount.value!
  property.numberOfOtherRooms = otherRoomCount.value!
  property.numberOfReceptionRooms = livingRoomCount.value!
  property.yearOfBuild = approximateBuildYear.value!
  property.roofConstruction = roofMaterial.value!
  property.flatRoofPercentage = roofFlatness.value!
  property.externalWallConstruction = externalWallMaterial.value!
  property.hasSubsidence = (buildingsContentsOrBoth.value === DLDCommonValuesEnum.PropertyInsuranceTypeOfPolicy.Buildings || buildingsContentsOrBoth.value === DLDCommonValuesEnum.PropertyInsuranceTypeOfPolicy.BuildingsAndContents) ? anyHistoryOfSubsidence.value! === DLDCommonValuesEnum.Boolean.Yes : null
  property.hasBeenUnderpinned = (buildingsContentsOrBoth.value === DLDCommonValuesEnum.PropertyInsuranceTypeOfPolicy.Buildings || buildingsContentsOrBoth.value === DLDCommonValuesEnum.PropertyInsuranceTypeOfPolicy.BuildingsAndContents) ? anyHistoryOfUnderpinning.value! === DLDCommonValuesEnum.Boolean.Yes : null
  property.hasBeenFlooded = anyHistoryOfFlooding.value! === DLDCommonValuesEnum.Boolean.Yes
  property.anyCurrentBuildingWork = anyBuildingWorkInProgress.value! === DLDCommonValuesEnum.Boolean.Yes
  property.distanceFromWater = propertyDistanceToWatercourse.value!
  property.listedBuilding = propertyIsListedBuilding.value!
  property.isMainHome = mainHome.value!
  property.inGoodStateOfRepair = homeIsInGoodStateOfRepair.value! === DLDCommonValuesEnum.Boolean.Yes
  property.nearTrees = (buildingsContentsOrBoth.value === DLDCommonValuesEnum.PropertyInsuranceTypeOfPolicy.Buildings || buildingsContentsOrBoth.value === DLDCommonValuesEnum.PropertyInsuranceTypeOfPolicy.BuildingsAndContents) ? largeTreesNearby.value! === DLDCommonValuesEnum.Boolean.Yes : null
  property.costToRebuild = (buildingsContentsOrBoth.value === DLDCommonValuesEnum.PropertyInsuranceTypeOfPolicy.Buildings || buildingsContentsOrBoth.value === DLDCommonValuesEnum.PropertyInsuranceTypeOfPolicy.BuildingsAndContents) ? costToRebuildProperty.value! : null
  property.hasHighValueItems = (buildingsContentsOrBoth.value === DLDCommonValuesEnum.PropertyInsuranceTypeOfPolicy.Contents || buildingsContentsOrBoth.value === DLDCommonValuesEnum.PropertyInsuranceTypeOfPolicy.BuildingsAndContents) ? hasHighValueItems.value! === DLDCommonValuesEnum.Boolean.Yes : null
  property.hasUnspecifiedItems = (buildingsContentsOrBoth.value === DLDCommonValuesEnum.PropertyInsuranceTypeOfPolicy.Contents || buildingsContentsOrBoth.value === DLDCommonValuesEnum.PropertyInsuranceTypeOfPolicy.BuildingsAndContents) ? anyUnspecifiedItemsQuestion.value! === DLDCommonValuesEnum.Boolean.Yes : null
  property.unspecifiedItemsValue = (buildingsContentsOrBoth.value === DLDCommonValuesEnum.PropertyInsuranceTypeOfPolicy.Contents || buildingsContentsOrBoth.value === DLDCommonValuesEnum.PropertyInsuranceTypeOfPolicy.BuildingsAndContents)
    && anyUnspecifiedItemsQuestion.value! === DLDCommonValuesEnum.Boolean.Yes
    ? unspecifiedItemValue.value!
    : null!
  property.costToReplaceContents = (buildingsContentsOrBoth.value === DLDCommonValuesEnum.PropertyInsuranceTypeOfPolicy.Contents || buildingsContentsOrBoth.value === DLDCommonValuesEnum.PropertyInsuranceTypeOfPolicy.BuildingsAndContents) ? costToReplaceContents.value! : null!
  property.notYetPurchased = hasPurchasedProperty.value! === DLDCommonValuesEnum.Boolean.No
  property.purchasedDate = hasPurchasedProperty.value! === DLDCommonValuesEnum.Boolean.Yes ? dateOfPurchase.value! : null
  property.howLongAtProperty = hasPurchasedProperty.value! === DLDCommonValuesEnum.Boolean.No ? howLongAtProperty.value! : null
  property.whoLivesAtProperty = whoLivesAtProperty.value!
  property.howManyAdults = whoLivesAtProperty.value! === DLDCommonValuesEnum.PropertyInsuranceWhoLivesAtProperty.PolicyholderOnly ? null : howManyAdults.value!
  property.howManyChildren = whoLivesAtProperty.value! === DLDCommonValuesEnum.PropertyInsuranceWhoLivesAtProperty.PolicyholderOnly ? null : howManyChildren.value!
  property.animalsInProperty = anyAnimals.value!
  property.anySmokers = anySmokers.value! === DLDCommonValuesEnum.Boolean.Yes
  property.whenAreYouAtProperty = whenAtProperty.value!
  property.howLongIsPropertyUnoccupied = howLongPropertyUnoccupied.value!
  property.anyBusinessUse = anyBusinessUse.value!
  property.typeOfAlarm = typeOfAlarm.value!
  property.alarmProfessionallyInstalledMaintained = typeOfAlarm.value === null || typeOfAlarm.value! === DLDCommonValuesEnum.PropertyInsuranceTypeOfAlarm.NoAlarm ? null : alarmProfessionallyInstalledMaintained.value! === DLDCommonValuesEnum.Boolean.Yes
  property.typeOfLocks = typeOfLocks.value!
  property.lessSecureDoorType = lessSecureDoors.value!
  property.hasKeyOperatedLocksOnAllWindows = hasKeyOperatedLocksOnAllWindows.value! === DLDCommonValuesEnum.Boolean.Yes
  property.includeAccidentalDamageForBuildings = (buildingsContentsOrBoth.value === DLDCommonValuesEnum.PropertyInsuranceTypeOfPolicy.Buildings || buildingsContentsOrBoth.value === DLDCommonValuesEnum.PropertyInsuranceTypeOfPolicy.BuildingsAndContents) ? includeAccidentalDamageForBuildings.value! === DLDCommonValuesEnum.Boolean.Yes : null
  property.includeAccidentalDamageForContents = (buildingsContentsOrBoth.value === DLDCommonValuesEnum.PropertyInsuranceTypeOfPolicy.Contents || buildingsContentsOrBoth.value === DLDCommonValuesEnum.PropertyInsuranceTypeOfPolicy.BuildingsAndContents) ? includeAccidentalDamageForContents.value! === DLDCommonValuesEnum.Boolean.Yes : null
  property.workingSmokeAlarmType = workingSmokeDetectors.value!
  property.inVerifiedNeighbourhoodWatchArea = inVerifiedNeighbourhoodWatchArea?.value === DLDCommonValuesEnum.Boolean.Yes
  property.additionalExteriorDoorType = otherTypeOfLocks.value!
  property.specifiedItems = (buildingsContentsOrBoth.value === DLDCommonValuesEnum.PropertyInsuranceTypeOfPolicy.Contents || buildingsContentsOrBoth.value === DLDCommonValuesEnum.PropertyInsuranceTypeOfPolicy.BuildingsAndContents)
    && hasHighValueItems.value! === DLDCommonValuesEnum.Boolean.Yes
    ? getSpecifiedItems(questions)
    : []
  property.propertyClaims = hasClaims.value! === DLDCommonValuesEnum.Boolean.Yes ? getPropertyClaims(questions) : []

  quoteRequest.properties.push(property)

  // Additional policyholders
  const additionalPolicyholders = questions.find(q => q.name === QuestionEnum.additionalPolicyholders) as QuestionAnswer<string>

  if (additionalPolicyholders?.subPageAnswers?.length > 0) {
    const subPage = additionalPolicyholders.subPageAnswers[0]
    const apTitle = subPage.answers.find(q => q.name === QuestionEnum.title) as QuestionAnswer<string>
    const apFirstName = subPage.answers.find(q => q.name === QuestionEnum.firstname) as QuestionAnswer<string>
    const apLastName = subPage.answers.find(q => q.name === QuestionEnum.lastName) as QuestionAnswer<string>
    const apDateOfBirth = subPage.answers.find(q => q.name === QuestionEnum.dateOfBirth) as QuestionAnswer<Date>
    const apRelationshipToPolicyholder = subPage.answers.find(q => q.name === QuestionEnum.relationshipToPolicyHolder) as QuestionAnswer<string>
    const apEmploymentStatus = subPage.answers.find(q => q.name === QuestionEnum.employmentStatus) as QuestionAnswer<string>
    const apOccupation = subPage.answers.find(q => q.name === QuestionEnum.occupation) as QuestionAnswer<string>
    const apBusinessCategory = subPage.answers.find(q => q.name === QuestionEnum.businessCategory) as QuestionAnswer<string>
    const apSecondaryOccupation = subPage.answers.find(q => q.name === QuestionEnum.secondaryOccupation) as QuestionAnswer<string>
    const apSecondaryBusinessCategory = subPage.answers.find(q => q.name === QuestionEnum.secondaryBusinessCategory) as QuestionAnswer<string>
    const apMaritalStatus = subPage.answers.find(q => q.name === QuestionEnum.maritalStatus) as QuestionAnswer<string>
    const apHasSecondaryEmployment = subPage.answers.find(q => q.name === QuestionEnum.hasSecondaryEmployment) as QuestionAnswer<string>
    const apStudentType = subPage.answers.find(q => q.name === QuestionEnum.studentType) as QuestionAnswer<string>
    const apPrn = subPage.answers.find(q => q.name === QuestionEnum.PRN) as QuestionAnswer<number>

    if (apEmploymentStatus?.value === DLDCommonValuesEnum.EmploymentStatus.Retired) {
      if (apOccupation)
        apOccupation.value = DLDCommonValuesEnum.Occupation.Retired
      if (apBusinessCategory)
        apBusinessCategory.value = DLDCommonValuesEnum.BusinessCategory.NonRetired
    }
    else if (apEmploymentStatus?.value === DLDCommonValuesEnum.EmploymentStatus.Unemployed) {
      if (apOccupation)
        apOccupation.value = DLDCommonValuesEnum.Occupation.Unemployed
      if (apBusinessCategory)
        apBusinessCategory.value = DLDCommonValuesEnum.BusinessCategory.NonUnemployed
    }
    else if (apEmploymentStatus?.value === DLDCommonValuesEnum.EmploymentStatus.FinanciallyAssisted || apEmploymentStatus?.value === DLDCommonValuesEnum.EmploymentStatus.IndependentMeans) {
      if (apOccupation)
        apOccupation.value = DLDCommonValuesEnum.Occupation.NotInEmployment
      if (apBusinessCategory)
        apBusinessCategory.value = DLDCommonValuesEnum.BusinessCategory.NotInEmployment
    }
    else if (apEmploymentStatus?.value === DLDCommonValuesEnum.EmploymentStatus.NotEmployedDueToDisability) {
      if (apOccupation)
        apOccupation.value = DLDCommonValuesEnum.Occupation.NotEmployedDueToDisability
      if (apBusinessCategory)
        apBusinessCategory.value = DLDCommonValuesEnum.BusinessCategory.NoneNotEmployedduetoDisability
    }
    else if (apEmploymentStatus?.value === DLDCommonValuesEnum.EmploymentStatus.HouseholdDuties) {
      if (apOccupation) {
        apOccupation.value = (apTitle.value === DLDCommonValuesEnum.Title.Mr || apTitle.value === DLDCommonValuesEnum.Title.DoctorMale)
          ? DLDCommonValuesEnum.Occupation.Househusband
          : DLDCommonValuesEnum.Occupation.Housewife
      }
      if (apBusinessCategory)
        apBusinessCategory.value = DLDCommonValuesEnum.BusinessCategory.NoneHouseholdDuties
    }

    if (apEmploymentStatus?.value === DLDCommonValuesEnum.EmploymentStatus.FullOrPartTimeEducation) {
      if (apOccupation)
        apOccupation.value = apStudentType.value!
      if (apBusinessCategory)
        apBusinessCategory.value = DLDCommonValuesEnum.BusinessCategory.NoneStudent
    }
    else if (apOccupation?.value?.toLowerCase().includes('student')) {
      if (apBusinessCategory)
        apBusinessCategory.value = DLDCommonValuesEnum.BusinessCategory.NoneStudent
    }
    else {
      if (apStudentType)
        apStudentType.value = null
    }

    quoteRequest.additionalPolicyholder = new AdditionalPolicyholder()
    quoteRequest.additionalPolicyholder.title = apTitle.value!
    quoteRequest.additionalPolicyholder.firstName = apFirstName.value!
    quoteRequest.additionalPolicyholder.lastName = apLastName.value!
    quoteRequest.additionalPolicyholder.dateOfBirth = apDateOfBirth.value!
    quoteRequest.additionalPolicyholder.relationshiptoPolicyholder = apRelationshipToPolicyholder.value!
    quoteRequest.additionalPolicyholder.employmentStatus = apEmploymentStatus.value!
    quoteRequest.additionalPolicyholder.occupation = apOccupation.value!
    quoteRequest.additionalPolicyholder.businessType = apBusinessCategory.value!
    quoteRequest.additionalPolicyholder.maritalStatus = apMaritalStatus?.value
    quoteRequest.additionalPolicyholder.hasSecondaryEmployment = apHasSecondaryEmployment.value! === DLDCommonValuesEnum.Boolean.Yes
    quoteRequest.additionalPolicyholder.secondaryOccupation = apHasSecondaryEmployment.value! === DLDCommonValuesEnum.Boolean.Yes ? apSecondaryOccupation.value! : null
    quoteRequest.additionalPolicyholder.secondaryBusinessType = apHasSecondaryEmployment.value! === DLDCommonValuesEnum.Boolean.Yes ? apSecondaryBusinessCategory.value! : null
    quoteRequest.additionalPolicyholder.studentType = apStudentType.value!
    quoteRequest.additionalPolicyholder.prn = apPrn?.value ?? null
  }

  return quoteRequest
}

function getPropertyClaims(questions: IQuestionAnswer[]): PropertyClaim[] {
  const claimsQuestion = questions.find(q => q.name === QuestionEnum.claims) as QuestionAnswer<ClaimsQuestion>
  const claims: PropertyClaim[] = []

  if (claimsQuestion) {
    for (let i = 0; i < claimsQuestion.subPageAnswers.length; i += 1) {
      const claimDate = claimsQuestion.subPageAnswers[i].answers.find(q => q.name === QuestionEnum.claimDate) as QuestionAnswer<Date>
      const claimType = claimsQuestion.subPageAnswers[i].answers.find(q => q.name === QuestionEnum.claimType) as QuestionAnswer<string>
      const claimStatus = claimsQuestion.subPageAnswers[i].answers.find(q => q.name === QuestionEnum.claimStatus) as QuestionAnswer<string>
      const claimCause = claimsQuestion.subPageAnswers[i].answers.find(q => q.name === QuestionEnum.homeClaimCause) as QuestionAnswer<string>
      const claimAmount = claimsQuestion.subPageAnswers[i].answers.find(q => q.name === QuestionEnum.claimAmount) as QuestionAnswer<number>
      const claimHappenedAtProperty = claimsQuestion.subPageAnswers[i].answers.find(q => q.name === QuestionEnum.homeClaimAtCurrentProperty) as QuestionAnswer<string>
      const claimPrn = claimsQuestion.subPageAnswers[i].answers.find(q => q.name === QuestionEnum.PRN) as QuestionAnswer<number>

      claims.push({
        claimDate: claimDate.value!,
        claimType: claimType.value!,
        claimStatus: claimStatus.value!,
        value: claimAmount.value!,
        happenedAtProperty: claimHappenedAtProperty.value! === DLDCommonValuesEnum.Boolean.Yes,
        claimCause: claimCause.value!,
        prn: claimPrn?.value ?? null,
      })
    }
  }
  return claims
}

function getSpecifiedItems(questions: IQuestionAnswer[]): SpecifiedItem[] {
  const specifiedItems = questions.find(q => q.name === QuestionEnum.specifiedItemsSubpage) as QuestionAnswer<boolean>
  const specItems: SpecifiedItem[] = []

  if (specifiedItems) {
    for (let i = 0; i < specifiedItems.subPageAnswers.length; i += 1) {
      const subPage = specifiedItems.subPageAnswers[i]
      const typeOfItem = subPage.answers.find(q => q.name === QuestionEnum.typeOfItem) as QuestionAnswer<string>
      const insuredItemsCarriedOutsideProperty = subPage.answers.find(q => q.name === QuestionEnum.insuredItemsCarriedOutsideProperty) as QuestionAnswer<string>
      const replacementCost = subPage.answers.find(q => q.name === QuestionEnum.replacementCost) as QuestionAnswer<number>
      const specifiedItemDescription = subPage.answers.find(q => q.name === QuestionEnum.specifiedItemDescription) as QuestionAnswer<string>
      const specifiedItemPrn = subPage.answers.find(q => q.name === QuestionEnum.PRN) as QuestionAnswer<number>

      specItems.push({
        typeOfItem: typeOfItem.value!,
        insuredItemsCarriedOutsideProperty: insuredItemsCarriedOutsideProperty.value!,
        replacementCost: replacementCost.value!,
        specifiedItemDescription: specifiedItemDescription.value!,
        prn: specifiedItemPrn?.value ?? null,
      })
    }
  }

  return specItems
}

export function mapPropertyRiskToAnswers(risk: PropertyRisk, account: Account, pages: Page[]): Dictionary<PageAnswers> {
  const pageAnswers: Dictionary<PageAnswers> = {}
  mapAccountToAnswers(pageAnswers, account, pages)

  // Proposer Details and Cover
  createAnswerForQuestion(pageAnswers, QuestionEnum.title, risk.riskProposer.title?.uniqueId, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.firstname, risk.riskProposer.firstName, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.lastName, risk.riskProposer.lastName, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.dateOfBirth, risk.riskProposer.dateOfBirth, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.proposerAddress, risk.riskProposer.address, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.sameAsHomeAddress, risk.address.postCode === risk.riskProposer.address.postCode && risk.address.line1 === risk.riskProposer.address.line1 ? DLDCommonValuesEnum.Boolean.Yes : DLDCommonValuesEnum.Boolean.No, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.maritalStatus, risk.riskProposer.maritalStatus?.uniqueId, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.employmentStatus, risk.riskProposer.employmentStatus?.uniqueId, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.occupation, risk.riskProposer.occupation?.uniqueId, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.businessCategory, risk.riskProposer.businessType?.uniqueId, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.studentType, risk.riskProposer.studentType?.uniqueId, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.hasSecondaryEmployment, risk.riskProposer.hasSecondaryEmployment ? DLDCommonValuesEnum.Boolean.Yes : DLDCommonValuesEnum.Boolean.No, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.secondaryOccupation, risk.riskProposer.secondaryOccupation?.uniqueId, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.secondaryBusinessCategory, risk.riskProposer.secondaryBusinessType?.uniqueId, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.mobilePhoneNumber, risk.riskProposer.telephoneNumber, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.customerEmail, risk.riskProposer.emailAddress, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.everDeclaredBankrupt, risk.riskProposer.hasDeclaredBankruptcy ? DLDCommonValuesEnum.Boolean.Yes : DLDCommonValuesEnum.Boolean.No, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.hadSpecialConditions, risk.riskProposer.hadSpecialTermsImposed ? DLDCommonValuesEnum.Boolean.Yes : DLDCommonValuesEnum.Boolean.No, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.insuranceDeclined, risk.riskProposer.hadInsuranceRefusedCancelledOrDeclined ? DLDCommonValuesEnum.Boolean.Yes : DLDCommonValuesEnum.Boolean.No, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.hasCriminalConvictions, risk.riskProposer.hasUnspentNonMotoringConvictions ? DLDCommonValuesEnum.Boolean.Yes : DLDCommonValuesEnum.Boolean.No, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.hasClaims, risk.riskProposer.hasClaims ? DLDCommonValuesEnum.Boolean.Yes : DLDCommonValuesEnum.Boolean.No, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.yearsWithNoClaimsBuilding, risk.ncdBuildings?.uniqueId, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.yearsWithNoClaimsContents, risk.ncdContents?.uniqueId, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.buildingsVoluntaryExcess, risk.riskProposer.voluntaryExcess?.uniqueId, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.contentsVoluntaryExcess, risk.riskProposer.voluntaryExcess2?.uniqueId, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.coverEffectiveDate, risk.effectiveDate, pages)

  // Property Details
  const property = risk.properties[0]

  createAnswerForQuestion(pageAnswers, QuestionEnum.address, property.address, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.homeOwnedOrRented, property.own?.uniqueId, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.buildingsContentsOrBoth, property.typeOfPolicy?.uniqueId, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.propertyType, property.propertyType?.uniqueId, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.homeType, property.subType?.uniqueId, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.bedroomCount, property.numberOfBedrooms?.uniqueId, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.bathroomCount, property.numberOfBathrooms?.uniqueId, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.livingRoomCount, property.numberOfReceptionRooms?.uniqueId, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.otherRoomCount, property.numberOfOtherRooms?.uniqueId, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.approximateBuildYear, property.yearOfBuild, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.roofMaterial, property.roofConstruction?.uniqueId, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.roofFlatness, property.flatRoofPercentage?.uniqueId, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.externalWallMaterial, property.externalWallConstruction?.uniqueId, pages)
  if (property.hasSubsidence !== null)
    createAnswerForQuestion(pageAnswers, QuestionEnum.anyHistoryOfSubsidence, property.hasSubsidence ? DLDCommonValuesEnum.Boolean.Yes : DLDCommonValuesEnum.Boolean.No, pages)
  if (property.hasBeenUnderpinned !== null)
    createAnswerForQuestion(pageAnswers, QuestionEnum.anyHistoryOfUnderpinning, property.hasBeenUnderpinned ? DLDCommonValuesEnum.Boolean.Yes : DLDCommonValuesEnum.Boolean.No, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.anyHistoryOfFlooding, property.hasBeenFlooded ? DLDCommonValuesEnum.Boolean.Yes : DLDCommonValuesEnum.Boolean.No, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.anyBuildingWorkInProgress, property.anyCurrentBuildingWork ? DLDCommonValuesEnum.Boolean.Yes : DLDCommonValuesEnum.Boolean.No, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.propertyDistanceToWatercourse, property.distanceFromWater?.uniqueId, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.propertyIsListedBuilding, property.listedBuilding?.uniqueId, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.mainHome, property.isMainHome?.uniqueId, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.homeIsInGoodStateOfRepair, property.inGoodStateOfRepair ? DLDCommonValuesEnum.Boolean.Yes : DLDCommonValuesEnum.Boolean.No, pages)
  if (property.nearTrees !== null)
    createAnswerForQuestion(pageAnswers, QuestionEnum.largeTreesNearby, property.nearTrees ? DLDCommonValuesEnum.Boolean.Yes : DLDCommonValuesEnum.Boolean.No, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.costToRebuildProperty, property.costToRebuild, pages)

  if (property.includeAccidentalDamageForBuildings !== null)
    createAnswerForQuestion(pageAnswers, QuestionEnum.includeAccidentalDamageForBuildings, property.includeAccidentalDamageForBuildings ? DLDCommonValuesEnum.Boolean.Yes : DLDCommonValuesEnum.Boolean.No, pages)
  if (property.includeAccidentalDamageForContents !== null)
    createAnswerForQuestion(pageAnswers, QuestionEnum.includeAccidentalDamageForContents, property.includeAccidentalDamageForContents ? DLDCommonValuesEnum.Boolean.Yes : DLDCommonValuesEnum.Boolean.No, pages)

  // Home Contents Occupancy and Security
  if (property.hasHighValueItems !== null)
    createAnswerForQuestion(pageAnswers, QuestionEnum.hasHighValueItems, property.hasHighValueItems ? DLDCommonValuesEnum.Boolean.Yes : DLDCommonValuesEnum.Boolean.No, pages)
  if (property.hasUnspecifiedItems !== null)
    createAnswerForQuestion(pageAnswers, QuestionEnum.anyUnspecifiedItems, property.hasUnspecifiedItems ? DLDCommonValuesEnum.Boolean.Yes : DLDCommonValuesEnum.Boolean.No, pages)
  if (property.alarmProfessionallyInstalledMaintained !== null)
    createAnswerForQuestion(pageAnswers, QuestionEnum.alarmProfessionallyInstalledMaintained, property.alarmProfessionallyInstalledMaintained ? DLDCommonValuesEnum.Boolean.Yes : DLDCommonValuesEnum.Boolean.No, pages)

  createAnswerForQuestion(pageAnswers, QuestionEnum.unspecifiedItemValue, property.unspecifiedItemsValue?.uniqueId, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.costToReplaceContents, property.costToReplaceContents, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.hasPurchasedProperty, property.notYetPurchased ? DLDCommonValuesEnum.Boolean.No : DLDCommonValuesEnum.Boolean.Yes, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.dateOfPurchase, property.purchasedDate, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.howLongHaveYouLivedAtTheProperty, property.howLongAtProperty?.uniqueId, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.whoLivesAtProperty, property.whoLivesAtProperty?.uniqueId, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.howManyAdults, property.howManyAdults?.uniqueId, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.howManyChildren, property.howManyChildren?.uniqueId, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.anyAnimals, property.animalsInProperty?.uniqueId, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.anySmokers, property.anySmokers ? DLDCommonValuesEnum.Boolean.Yes : DLDCommonValuesEnum.Boolean.No, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.whenAtProperty, property.whenAreYouAtProperty?.uniqueId, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.howLongPropertyUnoccupied, property.howLongIsPropertyUnoccupied?.uniqueId, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.anyBusinessUse, property.anyBusinessUse?.uniqueId, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.typeOfAlarm, property.typeOfAlarm?.uniqueId, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.typeOfLocks, property.typeOfLocks?.uniqueId, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.lessSecureDoors, property.lessSecureDoorType?.uniqueId, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.otherTypeOfLocks, property.additionalExteriorDoorType?.uniqueId, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.hasKeyOperatedLocksOnAllWindows, property.hasKeyOperatedLocksOnAllWindows ? DLDCommonValuesEnum.Boolean.Yes : DLDCommonValuesEnum.Boolean.No, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.workingSmokeDetectors, property.workingSmokeAlarmType?.uniqueId, pages)
  createAnswerForQuestion(pageAnswers, QuestionEnum.inVerifiedNeighbourhoodWatchArea, property.inVerifiedNeighbourhoodWatchArea ? DLDCommonValuesEnum.Boolean.Yes : DLDCommonValuesEnum.Boolean.No, pages)

  const propertyClaims = createAnswerForQuestion(pageAnswers, QuestionEnum.claims, property.propertyClaims.length, pages)!
  propertyClaims.forEach((pca) => {
    for (let i = 0; i < property.propertyClaims.length; i += 1) {
      const pc = property.propertyClaims[i]
      const pcClaimDate = new QuestionAnswer<Date>(0, QuestionEnum.claimDate, '', pc.claimDate)
      const pcClaimType = new QuestionAnswer<string>(0, QuestionEnum.claimType, '', pc.claimType?.uniqueId)
      const pcClaimStatus = new QuestionAnswer<string>(0, QuestionEnum.claimStatus, '', pc.claimStatus?.uniqueId)
      const pcClaimCause = new QuestionAnswer<string>(0, QuestionEnum.homeClaimCause, '', pc.claimCause?.uniqueId)
      const pcHappenedAtProperty = new QuestionAnswer<string>(0, QuestionEnum.homeClaimAtCurrentProperty, '', pc.happenedAtProperty ? DLDCommonValuesEnum.Boolean.Yes : DLDCommonValuesEnum.Boolean.No)
      const pcClaimAmount = new QuestionAnswer<number>(0, QuestionEnum.claimAmount, '', pc.value)
      const pcPrn = new QuestionAnswer<number>(0, QuestionEnum.PRN, '', pc.prn, false)

      const adPage = new PageAnswers(`Claim ${i + 1}`, [pcClaimDate, pcClaimType, pcClaimStatus, pcClaimCause, pcHappenedAtProperty, pcClaimAmount, pcPrn])

      pca.subPageAnswers.push(adPage)
    }
  })

  // Additional policy holder
  if (risk.additionalPolicyholders.length > 0) {
    const additionalPolicyHolder = createAnswerForQuestion(pageAnswers, QuestionEnum.additionalPolicyholders, risk.additionalPolicyholders.length, pages)!
    const ap = risk.additionalPolicyholders[0]
    const apTitle = new QuestionAnswer<string>(0, QuestionEnum.title, '', ap.title.uniqueId)
    const apFirstName = new QuestionAnswer<string>(0, QuestionEnum.firstname, '', ap.firstName)
    const apLastName = new QuestionAnswer<string>(0, QuestionEnum.lastName, '', ap.lastName)
    const apDateOfBirth = new QuestionAnswer<Date>(0, QuestionEnum.dateOfBirth, '', ap.dateOfBirth)
    const apMaritalStatus = new QuestionAnswer<string>(0, QuestionEnum.maritalStatus, '', ap.maritalStatus?.uniqueId)
    const apRelationshipToPolicyholder = new QuestionAnswer<string>(0, QuestionEnum.relationshipToPolicyHolder, '', ap.relationshipToPolicyholder.uniqueId)
    const apEmploymentStatus = new QuestionAnswer<string>(0, QuestionEnum.employmentStatus, '', ap.employmentStatus.uniqueId)
    const apOccupation = new QuestionAnswer<string>(0, QuestionEnum.occupation, '', ap.occupation.uniqueId)
    const apBusinessCategory = new QuestionAnswer<string>(0, QuestionEnum.businessCategory, '', ap.businessType.uniqueId)
    const apStudentType = new QuestionAnswer<string>(0, QuestionEnum.studentType, '', ap.studentType.uniqueId)
    const apHasSecondaryEmployment = new QuestionAnswer<string>(0, QuestionEnum.hasSecondaryEmployment, '', ap.hasSecondaryEmployment ? DLDCommonValuesEnum.Boolean.Yes : DLDCommonValuesEnum.Boolean.No)
    const apSecondaryBusinessCategory = new QuestionAnswer<string>(0, QuestionEnum.secondaryBusinessCategory, '', ap.secondaryBusinessType.uniqueId)
    const apSecondaryOccupation = new QuestionAnswer<string>(0, QuestionEnum.secondaryOccupation, '', ap.secondaryOccupation.uniqueId)
    const apPrn = new QuestionAnswer<number>(0, QuestionEnum.PRN, '', ap.prn, false)
    const apPage = new PageAnswers(`Additional Proposer`, [apTitle, apFirstName, apLastName, apDateOfBirth, apRelationshipToPolicyholder, apEmploymentStatus, apOccupation, apBusinessCategory, apMaritalStatus, apHasSecondaryEmployment, apSecondaryBusinessCategory, apSecondaryOccupation, apStudentType, apPrn])
    additionalPolicyHolder[0].subPageAnswers.push(apPage)
  }

  if (property.specifiedItems !== null) {
    const specifiedItems = createAnswerForQuestion(pageAnswers, QuestionEnum.specifiedItemsSubpage, property.specifiedItems!.length, pages)!
    specifiedItems.forEach((spi) => {
      for (let i = 0; i < property.specifiedItems.length; i += 1) {
        const specItem = property.specifiedItems![i]
        if (specItem !== undefined && specItem.typeOfItem !== undefined && specItem.insuredItemsCarriedOutsideProperty !== undefined && specItem.replacementCost !== undefined && specItem.specifiedItemDescription !== undefined) {
          const typeOfItem = new QuestionAnswer<string>(0, QuestionEnum.typeOfItem, '', specItem.typeOfItem.uniqueId)
          const insuredItemsCarriedOutsideProperty = new QuestionAnswer<string>(1, QuestionEnum.insuredItemsCarriedOutsideProperty, '', `${specItem.insuredItemsCarriedOutsideProperty ? DLDCommonValuesEnum.Boolean.Yes : DLDCommonValuesEnum.Boolean.No}`)
          const replacementCost = new QuestionAnswer<number>(2, QuestionEnum.replacementCost, '', specItem.replacementCost)
          const specifiedItemDescription = new QuestionAnswer<string>(3, QuestionEnum.specifiedItemDescription, '', specItem.specifiedItemDescription)
          const specifiedItemPrn = new QuestionAnswer<number>(0, QuestionEnum.PRN, '', specItem.prn, false)

          const adPage = new PageAnswers(`Specified Item ${i + 1}`, [typeOfItem, insuredItemsCarriedOutsideProperty, replacementCost, specifiedItemDescription, specifiedItemPrn])
          spi.subPageAnswers.push(adPage)
        }
      }
    })
  }
  return pageAnswers
}
